import React from 'react';
import { BrowserRouter as Router, Routes, Route, Link, Outlet } from 'react-router-dom';
import { Provider } from 'react-redux';
import { store } from './store';
import { FileText, BarChart3, Library, Bot, LogOut, Settings } from 'lucide-react';
import MainContent from './components/MainContent';
import AIPage from './components/AIPage';
import { LoginPage, ProtectedRoute } from './components/auth';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import ThemeToggle from './components/ThemeToggle';
import DocumentsPage from './components/DocumentsPage';
import TemplatePage from './components/template/TemplatePage';
import TemplateEditor from './components/template/TemplateEditor';
import TemplateViewer from './components/template/TemplateViewer';

const Navigation = () => {
  const { logout } = useAuth();
  
  return (
    <nav className="w-full bg-black/30 backdrop-blur-md border-b border-purple-500/20">
      <div className="max-w-[2000px] mx-auto px-4 sm:px-6 lg:px-8 w-full">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center space-x-8">
            <Link to="/" className="flex items-center">
              <FileText className="w-8 h-8 text-purple-400" />
              <span className="ml-2 text-xl font-bold text-white font-display">DocAnalyzer AI</span>
            </Link>
            
            <div className="hidden md:flex space-x-4">
              <Link to="/" className="text-purple-300 hover:text-purple-200 px-3 py-2 rounded-lg text-sm font-medium flex items-center hover:bg-purple-500/10">
                <BarChart3 className="w-4 h-4 mr-2" />
                Dashboard
              </Link>
              <Link to="/documents" className="text-purple-300 hover:text-purple-200 px-3 py-2 rounded-lg text-sm font-medium flex items-center hover:bg-purple-500/10">
                <Library className="w-4 h-4 mr-2" />
                Documents
              </Link>
              <Link to="/templates" className="text-purple-300 hover:text-purple-200 px-3 py-2 rounded-lg text-sm font-medium flex items-center hover:bg-purple-500/10">
                <Settings className="w-4 h-4 mr-2" />
                Templates
              </Link>
              <Link to="/ai" className="text-purple-300 hover:text-purple-200 px-3 py-2 rounded-lg text-sm font-medium flex items-center hover:bg-purple-500/10">
                <Bot className="w-4 h-4 mr-2" />
                AI Assistant
              </Link>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <ThemeToggle />
            <button
              onClick={logout}
              className="flex items-center gap-2 bg-purple-500/20 hover:bg-purple-500/30 text-purple-300 px-4 py-2 rounded-lg text-sm font-medium border border-purple-500/20"
            >
              <LogOut className="w-4 h-4" />
              Logout
            </button>
          </div>
        </div>
      </div>
    </nav>
  );
};

const App: React.FC = () => {
  return (
    <Provider store={store}>
      <Router>
        <ThemeProvider>
          <AuthProvider>
            <div className="flex flex-col min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 transition-colors duration-300">
              <Routes>
                <Route path="/login" element={<LoginPage />} />
                <Route element={
                  <ProtectedRoute>
                    <div className="flex-1">
                      <Navigation />
                      <Outlet />
                      <footer className="w-full bg-black/30 border-t border-purple-500/20 py-8 mt-auto">
                        <div className="max-w-[2000px] mx-auto px-4 sm:px-6 lg:px-8">
                          <div className="text-center text-purple-400/60 text-sm">
                            © 2024 DocAnalyzer AI. Advanced document analysis powered by artificial intelligence.
                          </div>
                        </div>
                      </footer>
                    </div>
                  </ProtectedRoute>
                }>
                  <Route path="/" element={<MainContent />} />
                  <Route path="/ai" element={<AIPage />} />
                  <Route path="/documents" element={<DocumentsPage />} />
                  <Route path="/templates">
                    <Route index element={<TemplatePage />} />
                    <Route path="new" element={<TemplateEditor />} />
                    <Route path=":id" element={<TemplateViewer />} />
                    <Route path=":id/edit" element={<TemplateEditor />} />
                  </Route>
                </Route>
              </Routes>
            </div>
          </AuthProvider>
        </ThemeProvider>
      </Router>
    </Provider>
  );
};

export default App;